import React, { useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import {
  Search,
  SlidersHorizontal,
  Sparkles,
  PackageSearch,
  Layers,
  Bug,
  Settings,
  X,
  Heart,
  ShoppingCart,
  UserCircle2,
  Store,
} from 'lucide-react';
import { API_BASE_URL } from '../config';
import { useAuth } from '../contexts/AuthContext';

const INITIAL_VISIBLE_PRODUCTS = 12;
const MAX_SERVER_TOP_K = 200;

const parseAttributes = (attrs) => {
  if (!Array.isArray(attrs)) return [];
  return attrs
    .map((a) => {
      if (!a || typeof a !== 'object') return null;
      const name = String(a.name || '').trim();
      const value = String(a.value || '').trim();
      if (!name || !value) return null;
      return { name, value };
    })
    .filter(Boolean);
};

const getHighlightPreview = (highlights) => {
  if (!highlights || typeof highlights !== 'object') return '';
  const fragments = Object.values(highlights)
    .flat()
    .filter((v) => typeof v === 'string' && v.trim())
    .slice(0, 2);
  if (!fragments.length) return '';
  return fragments
    .join(' ... ')
    .replace(/<mark>/g, '')
    .replace(/<\/mark>/g, '');
};

const ProductImage = ({ src, alt }) => {
  const [broken, setBroken] = useState(false);
  const imageUrl = String(src || '').trim();
  const hasImage = imageUrl && !broken;

  if (!hasImage) {
    return (
      <div className="product-image-placeholder" role="img" aria-label="No image available">
        No image
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt={alt}
      className="product-image"
      onError={() => setBroken(true)}
    />
  );
};

const OpenSearchEcommerce = () => {
  const { getAuthHeaders } = useAuth();
  const [query, setQuery] = useState('');
  const [smartSearchEnabled, setSmartSearchEnabled] = useState(false);
  const [category, setCategory] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [topK, setTopK] = useState(12);
  const [results, setResults] = useState([]);
  const [meta, setMeta] = useState({ query: '', count: 0, total_count: 0, index_name: '' });
  const [loading, setLoading] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsTesting, setSettingsTesting] = useState(false);
  const [connectionTestResult, setConnectionTestResult] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE_PRODUCTS);
  const [showFilters, setShowFilters] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [debugOpen, setDebugOpen] = useState(false);
  const [searchDebugHistory, setSearchDebugHistory] = useState([]);
  const [settingsWidthPct, setSettingsWidthPct] = useState(50);
  const [settingsResizing, setSettingsResizing] = useState(false);
  const settingsResizeRef = useRef({ startX: 0, startWidth: 50 });
  const [settingsForm, setSettingsForm] = useState({
    base_url: '',
    username: 'admin',
    password: '',
    verify_ssl: false,
    index_name: 'products',
    openai_api_key: '',
    openai_base_url: '',
    embedding_model: 'text-embedding-3-small',
    azure_endpoint: '',
    azure_api_version: '2024-02-01',
    azure_deployment: 'text-embedding-3-small',
    pipeline_name: 'hybrid_search_pipeline',
    agentic_model: 'gpt-4o-mini',
    agent_instructions: '',
    bm25_weight: 0.4,
    vector_weight: 0.6,
    default_top_k: 12,
    bulk_chunk_size: 25,
  });

  const cards = useMemo(
    () =>
      results.map((r, i) => {
        const attrs = parseAttributes(r.attributes).slice(0, 4);
        return {
          id: `${r.item_id || r.part_number || i}`,
          title: r.short_desc || r.part_number || 'Product',
          image: String(r.image || '').trim(),
          category: r.category || 'Uncategorized',
          manufacturer: r.manufacturer || 'Unknown',
          partNumber: r.part_number || 'N/A',
          score: Number(r.score || 0),
          attrs,
          highlight: getHighlightPreview(r.highlights),
        };
      }),
    [results]
  );
  const visibleBatchSize = useMemo(
    () => Math.max(1, Math.min(MAX_SERVER_TOP_K, Number(topK || INITIAL_VISIBLE_PRODUCTS))),
    [topK]
  );
  const visibleCards = useMemo(() => cards.slice(0, visibleCount), [cards, visibleCount]);
  const hasMoreCards = visibleCount < cards.length || cards.length < Number(meta.total_count || 0);

  const fetchSearchResults = async ({ q, requestedTopK, resetVisible = false }) => {
    const response = await fetch(`${API_BASE_URL}/api/v1/osearch_v2/search`, {
      method: 'POST',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: q,
        search_mode: smartSearchEnabled ? 'semantic' : 'hybrid',
        agentic_search: false,
        agentic_model: String(settingsForm.agentic_model || '').trim() || null,
        agent_instructions: String(settingsForm.agent_instructions || '').trim() || null,
        category: category.trim() || null,
        manufacturer: manufacturer.trim() || null,
        top_k: requestedTopK,
        bm25_weight: Number(settingsForm.bm25_weight || 0.4),
        vector_weight: Number(settingsForm.vector_weight || 0.6),
        pipeline_name: String(settingsForm.pipeline_name || 'hybrid_search_pipeline').trim() || 'hybrid_search_pipeline',
      }),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.detail || `Search failed (${response.status})`);
    }
    const nextResults = Array.isArray(data.results) ? data.results : [];
    setResults(nextResults);
    if (resetVisible) {
      setVisibleCount(Math.max(1, Math.min(visibleBatchSize, nextResults.length || visibleBatchSize)));
    }
    setMeta({
      query: String(data.query || q),
      count: Number(data.count || nextResults.length || 0),
      total_count: Number(data.total_count || nextResults.length || 0),
      index_name: String(data.index_name || ''),
    });
    const agenticDebug = data?.agentic_debug && typeof data.agentic_debug === 'object' ? data.agentic_debug : {};
    setSearchDebugHistory((prev) =>
      [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
          at: new Date().toISOString(),
          query: String(q || ''),
          searchMode: String(data?.search_mode || (smartSearchEnabled ? 'semantic' : 'hybrid')),
          agenticSearch: Boolean(data?.agentic_search),
          agenticQuery: String(data?.agentic_query || q || ''),
          agenticModel: String(data?.agentic_model || settingsForm.agentic_model || ''),
          agenticMs: Number(data?.agentic_ms || 0),
          requestHeaders: agenticDebug?.request_headers || {},
          requestBody: agenticDebug?.request_body || {},
          response: agenticDebug?.response || {},
        },
        ...(Array.isArray(prev) ? prev : []),
      ].slice(0, 25)
    );
    return nextResults.length;
  };

  const asPrettyJson = (value) => {
    try {
      return JSON.stringify(value ?? {}, null, 2);
    } catch {
      return String(value ?? '');
    }
  };

  const runSearch = async (event) => {
    event?.preventDefault?.();
    const q = String(query || '').trim();
    if (!q) {
      toast.error('Enter a search query first.');
      return;
    }
    setLoading(true);
    setSubmitted(true);
    try {
      const requestedTopK = Math.max(1, Math.min(MAX_SERVER_TOP_K, Number(topK || INITIAL_VISIBLE_PRODUCTS)));
      await fetchSearchResults({ q, requestedTopK, resetVisible: true });
    } catch (error) {
      setResults([]);
      setMeta({ query: q, count: 0, total_count: 0, index_name: '' });
      toast.error(String(error?.message || error));
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    setSettingsLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/open-search/settings`, {
        headers: getAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.detail || `Failed to load settings (${response.status})`);
      }
      const nextSettings = { ...settingsForm, ...(data?.settings || {}) };
      setSettingsForm(nextSettings);
      if (!query) {
        const nextTopK = Math.max(1, Math.min(50, Number(nextSettings.default_top_k || 12)));
        setTopK(nextTopK);
      }
    } catch (error) {
      toast.error(String(error?.message || error));
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveSettings = async (event) => {
    event?.preventDefault?.();
    const bm25 = Number(settingsForm.bm25_weight || 0);
    const vector = Number(settingsForm.vector_weight || 0);
    if (Math.abs(bm25 + vector - 1) > 0.0001) {
      toast.error('BM25 and Vector weights must add up to 1.0');
      return;
    }
    setSettingsSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/open-search/settings`, {
        method: 'PUT',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settingsForm),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.detail || `Failed to save settings (${response.status})`);
      }
      const saved = { ...settingsForm, ...(data?.settings || {}) };
      setSettingsForm(saved);
      setTopK(Math.max(1, Math.min(50, Number(saved.default_top_k || topK))));
      toast.success('Search settings saved');
      setSettingsOpen(false);
    } catch (error) {
      toast.error(String(error?.message || error));
    } finally {
      setSettingsSaving(false);
    }
  };

  const testConnection = async () => {
    const bm25 = Number(settingsForm.bm25_weight || 0);
    const vector = Number(settingsForm.vector_weight || 0);
    if (Math.abs(bm25 + vector - 1) > 0.0001) {
      toast.error('BM25 and Vector weights must add up to 1.0 before testing');
      return;
    }
    setSettingsTesting(true);
    setConnectionTestResult(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/v1/open-search/test-connection`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settingsForm),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data?.detail || `Connection test failed (${response.status})`);
      }
      setConnectionTestResult(data);
      if (data?.ok) {
        toast.success('Connection test passed');
      } else {
        toast.error('Connection test failed');
      }
    } catch (error) {
      setConnectionTestResult({
        ok: false,
        opensearch: { ok: false, error: String(error?.message || error) },
        embedding: { ok: false, error: 'Test not completed' },
      });
      toast.error(String(error?.message || error));
    } finally {
      setSettingsTesting(false);
    }
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!settingsResizing) return undefined;
    const onMove = (event) => {
      const delta = settingsResizeRef.current.startX - event.clientX;
      const rawWidth = settingsResizeRef.current.startWidth + (delta / window.innerWidth) * 100;
      const constrained = Math.max(30, Math.min(75, rawWidth));
      setSettingsWidthPct(constrained);
    };
    const onUp = () => setSettingsResizing(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [settingsResizing]);

  useEffect(() => {
    if (!loading && submitted && cards.length < visibleCount) {
      setVisibleCount(Math.max(cards.length, visibleBatchSize));
    }
  }, [cards.length, loading, submitted, visibleBatchSize, visibleCount]);

  return (
    <div className="container-fluid py-3 py-md-4 px-2 px-md-3 px-xl-4 open-search-ecom-page">
      <header className="ecom-page-header card border-0 shadow-sm mb-3 mb-lg-4">
        <div className="card-body p-2 p-md-3">
          <div className="row g-2 g-md-3 align-items-center">
            <div className="col-12 col-xl-3">
              <div className="d-flex align-items-center gap-2 gap-md-3 px-2 py-1">
                <div className="brand-logo">
                  <Store size={18} />
                </div>
                <div>
                  <div className="fw-bold lh-1">OpenSearch Shop</div>
                  <div className="small text-muted">Hybrid product discovery</div>
                </div>
              </div>
            </div>
            <div className="col-12 col-xl-6">
              <form onSubmit={runSearch}>
                <div className="d-flex align-items-center gap-2 smart-search-inline-wrap">
                  <div className="input-group header-search-group smart-search-inline-input">
                    <span className="input-group-text bg-white border-0 ps-3">
                      <Search size={16} />
                    </span>
                    <input
                      type="text"
                      className="form-control border-0"
                      placeholder="Search products, part numbers, manufacturers..."
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                    />
                    <button type="submit" className="btn btn-dark px-3 px-md-4" disabled={loading}>
                      {loading ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                  <div className="form-check form-switch mb-0 d-inline-flex align-items-center gap-2 smart-search-toggle px-1">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="smartSearchToggle"
                      checked={smartSearchEnabled}
                      onChange={(e) => setSmartSearchEnabled(e.target.checked)}
                    />
                    <label className="form-check-label small fw-semibold" htmlFor="smartSearchToggle">
                      Smart Search
                    </label>
                  </div>
                </div>
              </form>
            </div>
            <div className="col-12 col-xl-3">
              <div className="d-flex align-items-center justify-content-xl-end gap-2 px-2">
                <button type="button" className="btn btn-icon btn-outline-secondary" title="Wishlist">
                  <Heart size={16} />
                </button>
                <button type="button" className="btn btn-icon btn-outline-secondary" title="Cart">
                  <ShoppingCart size={16} />
                </button>
                <button type="button" className="btn btn-icon btn-outline-secondary" title="Profile">
                  <UserCircle2 size={16} />
                </button>
                <button
                  type="button"
                  className="btn btn-icon btn-outline-secondary"
                  title="Search Debug"
                  onClick={() => setDebugOpen(true)}
                >
                  <Bug size={16} />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-dark d-inline-flex align-items-center gap-1"
                  onClick={() => setSettingsOpen(true)}
                >
                  <Settings size={14} />
                  Settings
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="ecom-meta-strip card border-0 mb-3">
        <div className="card-body py-2 px-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div className="d-inline-flex align-items-center gap-2 badge rounded-pill text-bg-light">
            <Sparkles size={14} />
            {meta.query ? `Showing results for "${meta.query}"` : 'Try a search to explore products'}
          </div>
          <div className="d-flex align-items-center gap-3 small text-muted">
            <span>
              Smart Search: <strong>{smartSearchEnabled ? 'On (Semantic)' : 'Off (Hybrid)'}</strong>
            </span>
            <span className="text-truncate">
              Index: <strong>{meta.index_name || settingsForm.index_name || 'products'}</strong>
            </span>
            <span>
              Results: <strong>{meta.total_count || meta.count || 0}</strong>
            </span>
          </div>
        </div>
      </div>

      {settingsOpen && (
        <>
          <div className="open-search-settings-backdrop" onClick={() => setSettingsOpen(false)} />
          <aside className="open-search-settings-panel" style={{ width: `${settingsWidthPct}%` }}>
            <div
              className="open-search-settings-resizer"
              onMouseDown={(event) => {
                settingsResizeRef.current = { startX: event.clientX, startWidth: settingsWidthPct };
                setSettingsResizing(true);
              }}
            />
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <div>
                <h5 className="mb-0">OpenSearch Shop Settings</h5>
                <div className="small text-muted">Separate persisted settings for this page</div>
              </div>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setSettingsOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="p-3 p-md-4 settings-panel-scroll">
              <form className="row g-3" onSubmit={saveSettings}>
                <div className="col-12">
                  <label className="form-label small fw-semibold">OpenSearch Base URL</label>
                  <input
                    type="text"
                    className="form-control"
                    value={settingsForm.base_url}
                    onChange={(e) => setSettingsForm((p) => ({ ...p, base_url: e.target.value }))}
                    placeholder="https://dev-osearch.extremeb2b.com:8443"
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Docs per _bulk request</label>
                  <input
                    type="number"
                    min={1}
                    max={500}
                    className="form-control"
                    value={settingsForm.bulk_chunk_size ?? 25}
                    onChange={(e) =>
                      setSettingsForm((p) => ({
                        ...p,
                        bulk_chunk_size: Math.max(1, Math.min(500, Number(e.target.value) || 25)),
                      }))
                    }
                  />
                  <div className="form-text">Products indexing chunk size (1–500).</div>
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Username</label>
                  <input
                    type="text"
                    className="form-control"
                    value={settingsForm.username}
                    onChange={(e) => setSettingsForm((p) => ({ ...p, username: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={settingsForm.password}
                    onChange={(e) => setSettingsForm((p) => ({ ...p, password: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Index Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={settingsForm.index_name}
                    onChange={(e) => setSettingsForm((p) => ({ ...p, index_name: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Pipeline Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={settingsForm.pipeline_name}
                    onChange={(e) => setSettingsForm((p) => ({ ...p, pipeline_name: e.target.value }))}
                  />
                </div>
                <div className="col-12 mt-2">
                  <div className="border rounded-3 p-3 p-md-4 bg-light-subtle">
                    <div className="fw-semibold mb-1">Agentic Search Settings</div>
                    <div className="small text-muted mb-3">
                      Configure model and instruction prompt used for agentic search behavior.
                    </div>
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="form-label small fw-semibold">Agentic Model</label>
                        <input
                          type="text"
                          className="form-control"
                          value={settingsForm.agentic_model}
                          onChange={(e) => setSettingsForm((p) => ({ ...p, agentic_model: e.target.value }))}
                          placeholder="gpt-4o-mini"
                        />
                      </div>
                      <div className="col-12">
                        <label className="form-label small fw-semibold">Agent Instructions</label>
                        <textarea
                          className="form-control"
                          rows={4}
                          value={settingsForm.agent_instructions}
                          onChange={(e) => setSettingsForm((p) => ({ ...p, agent_instructions: e.target.value }))}
                          placeholder="Describe how the agent should interpret user queries and ranking priorities."
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12">
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      id="osVerifySsl"
                      checked={Boolean(settingsForm.verify_ssl)}
                      onChange={(e) => setSettingsForm((p) => ({ ...p, verify_ssl: e.target.checked }))}
                    />
                    <label className="form-check-label" htmlFor="osVerifySsl">Verify SSL</label>
                  </div>
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">OpenAI API Key</label>
                  <input
                    type="password"
                    className="form-control"
                    value={settingsForm.openai_api_key}
                    onChange={(e) => setSettingsForm((p) => ({ ...p, openai_api_key: e.target.value }))}
                    placeholder="sk-..."
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">OpenAI Base URL (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={settingsForm.openai_base_url}
                    onChange={(e) => setSettingsForm((p) => ({ ...p, openai_base_url: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Embedding Model</label>
                  <input
                    type="text"
                    className="form-control"
                    value={settingsForm.embedding_model}
                    onChange={(e) => setSettingsForm((p) => ({ ...p, embedding_model: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Azure Endpoint (optional)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={settingsForm.azure_endpoint}
                    onChange={(e) => setSettingsForm((p) => ({ ...p, azure_endpoint: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Azure API Version</label>
                  <input
                    type="text"
                    className="form-control"
                    value={settingsForm.azure_api_version}
                    onChange={(e) => setSettingsForm((p) => ({ ...p, azure_api_version: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Azure Deployment</label>
                  <input
                    type="text"
                    className="form-control"
                    value={settingsForm.azure_deployment}
                    onChange={(e) => setSettingsForm((p) => ({ ...p, azure_deployment: e.target.value }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Default Top K</label>
                  <input
                    type="number"
                    min={1}
                    max={50}
                    className="form-control"
                    value={settingsForm.default_top_k}
                    onChange={(e) =>
                      setSettingsForm((p) => ({
                        ...p,
                        default_top_k: Math.max(1, Math.min(50, Number(e.target.value || 12))),
                      }))
                    }
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">BM25 Weight</label>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    max={1}
                    className="form-control"
                    value={settingsForm.bm25_weight}
                    onChange={(e) => setSettingsForm((p) => ({ ...p, bm25_weight: Number(e.target.value || 0) }))}
                  />
                </div>
                <div className="col-12 col-md-6">
                  <label className="form-label small fw-semibold">Vector Weight</label>
                  <input
                    type="number"
                    step="0.01"
                    min={0}
                    max={1}
                    className="form-control"
                    value={settingsForm.vector_weight}
                    onChange={(e) => setSettingsForm((p) => ({ ...p, vector_weight: Number(e.target.value || 0) }))}
                  />
                </div>
                <div className="col-12 d-flex flex-wrap gap-2 justify-content-end pt-2">
                  <button type="button" className="btn btn-outline-secondary" onClick={loadSettings} disabled={settingsLoading}>
                    {settingsLoading ? 'Loading...' : 'Reload'}
                  </button>
                  <button type="button" className="btn btn-outline-primary" onClick={testConnection} disabled={settingsTesting}>
                    {settingsTesting ? 'Testing...' : 'Test Connection'}
                  </button>
                  <button type="submit" className="btn btn-dark" disabled={settingsSaving}>
                    {settingsSaving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
                {connectionTestResult && (
                  <div className="col-12">
                    <div className={`alert mb-0 ${connectionTestResult.ok ? 'alert-success' : 'alert-danger'}`}>
                      <div className="fw-semibold mb-1">
                        {connectionTestResult.ok ? 'Connection test successful' : 'Connection test failed'}
                      </div>
                      <div className="small">
                        OpenSearch: {connectionTestResult?.opensearch?.ok ? 'OK' : `Error - ${connectionTestResult?.opensearch?.error || 'unknown'}`}
                      </div>
                      <div className="small">
                        Embedding: {connectionTestResult?.embedding?.ok
                          ? `OK (${connectionTestResult?.embedding?.provider || 'provider'} / ${connectionTestResult?.embedding?.dimensions || 0} dims)`
                          : `Error - ${connectionTestResult?.embedding?.error || 'unknown'}`}
                      </div>
                    </div>
                  </div>
                )}
              </form>
            </div>
          </aside>
        </>
      )}
      {debugOpen && (
        <>
          <div className="open-search-settings-backdrop" onClick={() => setDebugOpen(false)} />
          <aside className="open-search-debug-panel">
            <div className="d-flex align-items-center justify-content-between p-3 border-bottom">
              <div>
                <h5 className="mb-0">Search Debug</h5>
                <div className="small text-muted">OpenAI request and response for each search</div>
              </div>
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setDebugOpen(false)}>
                <X size={16} />
              </button>
            </div>
            <div className="p-3 p-md-4 settings-panel-scroll">
              {searchDebugHistory.length === 0 ? (
                <div className="alert alert-secondary mb-0">Run a search to capture debug details.</div>
              ) : (
                <div className="d-flex flex-column gap-3">
                  {searchDebugHistory.map((entry, index) => (
                    <div key={entry.id} className="card border">
                      <div className="card-body p-3">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                          <div className="fw-semibold">Search #{searchDebugHistory.length - index}</div>
                          <div className="small text-muted">{new Date(entry.at).toLocaleString()}</div>
                        </div>
                        <div className="small mb-2">
                          <span className="fw-semibold">User Query:</span> {entry.query}
                        </div>
                        <div className="small mb-2">
                          <span className="fw-semibold">Agentic Query:</span> {entry.agenticQuery}
                        </div>
                        <div className="small mb-3">
                          <span className="fw-semibold">Mode:</span> {String(entry.searchMode || '-')} |{' '}
                          <span className="fw-semibold">Model:</span> {entry.agenticModel || '-'} |{' '}
                          <span className="fw-semibold">Agentic:</span> {entry.agenticSearch ? 'On' : 'Off'} |{' '}
                          <span className="fw-semibold">Latency:</span> {entry.agenticMs} ms
                        </div>
                        <div className="small fw-semibold mb-1">OpenAI Request Headers</div>
                        <pre className="debug-json-box">{asPrettyJson(entry.requestHeaders)}</pre>
                        <div className="small fw-semibold mb-1 mt-2">OpenAI Request Body</div>
                        <pre className="debug-json-box">{asPrettyJson(entry.requestBody)}</pre>
                        <div className="small fw-semibold mb-1 mt-2">OpenAI Response</div>
                        <pre className="debug-json-box mb-0">{asPrettyJson(entry.response)}</pre>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </aside>
        </>
      )}

      <div className="d-flex flex-column flex-lg-row gap-3 gap-lg-4">
        <aside className={showFilters ? 'filter-panel-wrap' : 'd-none'}>
          <div className="card border-0 shadow-sm sticky-lg-top search-filter-sticky">
            <div className="card-body p-3 p-md-4">
              <div className="d-flex align-items-center gap-2 mb-3">
                <SlidersHorizontal size={16} />
                <h6 className="mb-0">Search Filters</h6>
              </div>
              <form className="row g-3" onSubmit={runSearch}>
                <div className="col-12">
                  <label className="form-label small fw-semibold">Category</label>
                  <input
                    type="text"
                    className="form-control"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    placeholder="Masking Tapes"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">Manufacturer</label>
                  <input
                    type="text"
                    className="form-control"
                    value={manufacturer}
                    onChange={(e) => setManufacturer(e.target.value)}
                    placeholder="Dixon Valve & Coupling"
                  />
                </div>
                <div className="col-12">
                  <label className="form-label small fw-semibold">Results Per Search</label>
                  <input
                    type="number"
                    min={1}
                    max={MAX_SERVER_TOP_K}
                    className="form-control"
                    value={topK}
                    onChange={(e) => setTopK(Math.max(1, Math.min(MAX_SERVER_TOP_K, Number(e.target.value || 12))))}
                  />
                </div>
                <div className="col-12">
                  <button className="btn btn-dark w-100" disabled={loading} type="submit">
                    {loading ? 'Searching...' : 'Search Products'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </aside>

        <section className="results-panel-wrap flex-grow-1">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
            <div>
              <h5 className="mb-1 d-flex align-items-center gap-2">
                <PackageSearch size={18} />
                Product Results
              </h5>
              <div className="text-muted small">
                {meta.query ? `Showing "${meta.query}"` : 'Run a query to view product matches'}
              </div>
            </div>
            <div className="small text-muted d-flex align-items-center gap-2">
              <button
                type="button"
                className={`btn btn-sm d-inline-flex align-items-center gap-1 ${
                  showFilters ? 'btn-dark' : 'btn-outline-dark'
                }`}
                onClick={() => setShowFilters((prev) => !prev)}
              >
                <SlidersHorizontal size={14} />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
              <span className="d-inline-flex align-items-center gap-1">
                <Layers size={14} />
                {meta.count || 0} items
              </span>
            </div>
          </div>

          {!loading && submitted && cards.length === 0 && (
            <div className="card border-0 shadow-sm">
              <div className="card-body py-5 text-center">
                <h6 className="mb-2">No products found</h6>
                <p className="text-muted mb-0">Try a broader keyword or remove one of the filters.</p>
              </div>
            </div>
          )}

          {!submitted && (
            <div className="card border-0 shadow-sm">
              <div className="card-body py-5 text-center">
                <h6 className="mb-2">Start your product search</h6>
                <p className="text-muted mb-0">
                  Enter query text on the left to load ecommerce-style cards with product specs.
                </p>
              </div>
            </div>
          )}

          <div className={`product-grid ${showFilters ? 'with-filters' : 'full-width'}`}>
            {visibleCards.map((item) => (
              <div key={item.id} className="product-grid-item">
                <article className="card border-0 shadow-sm h-100 product-card">
                  <div className="product-image-wrap">
                    <ProductImage src={item.image} alt={item.title} />
                    <span className="badge score-badge">Score {item.score.toFixed(2)}</span>
                  </div>
                  <div className="card-body p-3 d-flex flex-column">
                    <div className="d-flex align-items-center justify-content-between mb-1">
                      <div className="text-muted small text-truncate">{item.category}</div>
                      <div className="small text-muted product-score-inline">{item.score.toFixed(2)}</div>
                    </div>
                    <h6 className="mb-2 product-title">{item.title}</h6>
                    <div className="small text-muted mb-1">Brand: {item.manufacturer}</div>
                    <div className="small fw-semibold mb-2 text-break">Part #: {item.partNumber}</div>
                    {item.highlight && (
                      <div className="highlight-chip small mb-3" title={item.highlight}>
                        {item.highlight}
                      </div>
                    )}
                    <div className="mt-auto d-flex flex-wrap gap-1">
                      {item.attrs.length > 0 ? (
                        item.attrs.map((attr) => (
                          <span key={`${item.id}-${attr.name}`} className="badge attr-pill text-bg-light">
                            {attr.name}: {attr.value}
                          </span>
                        ))
                      ) : (
                        <span className="small text-muted">No attributes available</span>
                      )}
                    </div>
                    <div className="d-flex gap-2 mt-3">
                      <button type="button" className="btn btn-sm btn-outline-dark flex-grow-1">Quick View</button>
                      <button type="button" className="btn btn-sm btn-dark flex-grow-1">Add</button>
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </div>
          {!loading && submitted && hasMoreCards && (
            <div className="d-flex justify-content-center mt-4">
              <button
                type="button"
                className="btn btn-outline-dark px-4"
                onClick={async () => {
                  if (loading) return;
                  const localNextVisible = Math.min(visibleCount + visibleBatchSize, cards.length);
                  if (localNextVisible > visibleCount) {
                    setVisibleCount(localNextVisible);
                    return;
                  }
                  const totalCount = Number(meta.total_count || 0);
                  if (cards.length >= totalCount) return;

                  const q = String(query || '').trim();
                  if (!q) return;

                  setLoading(true);
                  try {
                    const requestedTopK = Math.min(MAX_SERVER_TOP_K, cards.length + visibleBatchSize);
                    const loadedCount = await fetchSearchResults({ q, requestedTopK, resetVisible: false });
                    setVisibleCount((prev) => Math.min(prev + visibleBatchSize, loadedCount));
                  } catch (error) {
                    toast.error(String(error?.message || error));
                  } finally {
                    setLoading(false);
                  }
                }}
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </section>
      </div>

      <style>{`
        .open-search-ecom-page .ecom-page-header {
          border-radius: 1rem;
          position: sticky;
          top: 0;
          z-index: 40;
          background: #e2e8f0;
          border: 1px solid #cbd5e1 !important;
        }
        .open-search-ecom-page .ecom-page-header .card-body {
          background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%);
          border-radius: 1rem;
        }
        .open-search-ecom-page .ecom-page-header,
        .open-search-ecom-page .ecom-page-header .text-muted,
        .open-search-ecom-page .ecom-page-header .fw-bold,
        .open-search-ecom-page .ecom-page-header svg {
          color: #0f172a !important;
        }
        .open-search-ecom-page .ecom-page-header .header-search-group {
          border: 1px solid #94a3b8;
          background: #ffffff;
        }
        .open-search-ecom-page .ecom-page-header .header-search-group .form-control,
        .open-search-ecom-page .ecom-page-header .header-search-group .input-group-text {
          background: #ffffff;
          color: #0f172a;
        }
        .open-search-ecom-page .ecom-page-header .header-search-group .form-control::placeholder {
          color: #64748b;
        }
        .open-search-ecom-page .smart-search-toggle .form-check-input {
          cursor: pointer;
        }
        .open-search-ecom-page .smart-search-toggle {
          padding-left: 0 !important;
        }
        .open-search-ecom-page .smart-search-toggle .form-check-input {
          float: none;
          margin-left: 0;
          margin-right: 0.25rem;
        }
        .open-search-ecom-page .smart-search-inline-wrap {
          min-width: 0;
        }
        .open-search-ecom-page .smart-search-inline-input {
          min-width: 0;
          flex: 1 1 auto;
        }
        .open-search-ecom-page .brand-logo {
          width: 36px;
          height: 36px;
          border-radius: 10px;
          background: #334155;
          color: #fff;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }
        .open-search-ecom-page .header-search-group {
          border: 1px solid #e2e8f0;
          border-radius: 0.65rem;
          overflow: hidden;
          background: #fff;
        }
        .open-search-ecom-page .header-search-group .form-control:focus {
          box-shadow: none;
        }
        .open-search-ecom-page .smart-search-toggle {
          white-space: nowrap;
        }
        .open-search-ecom-page .btn-icon {
          width: 36px;
          height: 36px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 999px;
          padding: 0;
        }
        .open-search-ecom-page .ecom-meta-strip {
          border-radius: 0.85rem;
          background: #f1f5f9;
          border: 1px solid #cbd5e1 !important;
        }
        .open-search-ecom-page .ecom-meta-strip .text-muted,
        .open-search-ecom-page .ecom-meta-strip .small,
        .open-search-ecom-page .ecom-meta-strip strong {
          color: #334155 !important;
        }
        .open-search-ecom-page .ecom-meta-strip .badge.text-bg-light {
          background: #e2e8f0 !important;
          color: #1e293b !important;
        }
        .open-search-ecom-page .product-card {
          border-radius: 0.85rem;
          overflow: hidden;
          transition: transform 0.2s ease, box-shadow 0.2s ease;
          border: 1px solid #edf2f7 !important;
        }
        .open-search-ecom-page .product-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 24px rgba(15, 23, 42, 0.1) !important;
        }
        .open-search-ecom-page .product-image-wrap {
          position: relative;
          width: 100%;
          height: 180px;
          min-height: 180px;
          max-height: 180px;
          background: #f8fafc;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .open-search-ecom-page .product-image {
          width: 100%;
          height: 100%;
          object-fit: contain;
          object-position: center;
          background: #f8fafc;
        }
        .open-search-ecom-page .product-image-placeholder {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: repeating-linear-gradient(
            45deg,
            #f1f5f9,
            #f1f5f9 14px,
            #e2e8f0 14px,
            #e2e8f0 28px
          );
          color: #475569;
          font-weight: 600;
          letter-spacing: 0.02em;
          text-transform: uppercase;
          font-size: 0.82rem;
        }
        .open-search-ecom-page .score-badge {
          position: absolute;
          top: 10px;
          right: 10px;
          background: #ffffff;
          color: #0f172a;
          border: 1px solid #e2e8f0;
        }
        .open-search-ecom-page .product-title {
          min-height: 3.2em;
          line-height: 1.35;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .open-search-ecom-page .highlight-chip {
          border-left: 2px solid #94a3b8;
          padding-left: 0.5rem;
          color: #334155;
          background: #f8fafc;
          border-radius: 0.35rem;
          padding-top: 0.25rem;
          padding-bottom: 0.25rem;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .open-search-ecom-page .attr-pill {
          font-weight: 400;
          border: 1px solid #e2e8f0;
          color: #334155;
          white-space: normal;
          text-align: left;
          max-width: 100%;
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .open-search-ecom-page .product-score-inline {
          font-size: 0.75rem;
        }
        .open-search-ecom-page .card-body .text-break {
          overflow-wrap: anywhere;
          word-break: break-word;
        }
        .open-search-ecom-page .product-card .card-body {
          min-height: 300px;
        }
        .open-search-ecom-page .product-card .btn {
          white-space: nowrap;
        }
        @media (max-width: 575.98px) {
          .open-search-ecom-page .product-image-wrap {
            height: 150px;
            min-height: 150px;
            max-height: 150px;
          }
          .open-search-ecom-page .product-card .btn {
            padding-left: 0.5rem;
            padding-right: 0.5rem;
            font-size: 0.8rem;
          }
          .open-search-ecom-page .product-card .card-body {
            min-height: 0;
          }
        }
        .open-search-settings-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(2, 6, 23, 0.35);
          z-index: 1200;
        }
        .open-search-settings-panel {
          position: fixed;
          right: 0;
          top: 0;
          height: 100vh;
          background: #fff;
          z-index: 1201;
          box-shadow: -18px 0 40px rgba(2, 6, 23, 0.2);
          display: flex;
          flex-direction: column;
          max-width: 100%;
        }
        .open-search-debug-panel {
          position: fixed;
          right: 0;
          top: 0;
          width: min(900px, 85vw);
          height: 100vh;
          background: #fff;
          z-index: 1201;
          box-shadow: -18px 0 40px rgba(2, 6, 23, 0.2);
          display: flex;
          flex-direction: column;
          max-width: 100%;
        }
        .settings-panel-scroll {
          overflow: auto;
          flex: 1;
        }
        .debug-json-box {
          background: #0f172a;
          color: #e2e8f0;
          border-radius: 0.5rem;
          padding: 0.65rem 0.75rem;
          font-size: 0.75rem;
          line-height: 1.35;
          max-height: 220px;
          overflow: auto;
          margin-bottom: 0.5rem;
        }
        .open-search-settings-resizer {
          position: absolute;
          left: -8px;
          top: 0;
          width: 8px;
          height: 100%;
          cursor: col-resize;
        }
        .open-search-ecom-page .search-filter-sticky {
          top: 92px !important;
          z-index: 20 !important;
        }
        .open-search-ecom-page .filter-panel-wrap {
          flex: 0 0 300px;
          max-width: 300px;
        }
        .open-search-ecom-page .results-panel-wrap {
          min-width: 0;
        }
        .open-search-ecom-page .product-grid {
          display: flex;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .open-search-ecom-page .product-grid .product-grid-item {
          flex: 1 1 100%;
          max-width: 100%;
        }
        @media (min-width: 576px) {
          .open-search-ecom-page .product-grid .product-grid-item {
            flex: 1 1 calc(50% - 1rem);
            max-width: calc(50% - 1rem);
          }
        }
        @media (min-width: 992px) {
          .open-search-ecom-page .product-grid.with-filters .product-grid-item {
            flex: 1 1 calc(33.333% - 1rem);
            max-width: calc(33.333% - 1rem);
          }
          .open-search-ecom-page .product-grid.full-width .product-grid-item {
            flex: 1 1 calc(25% - 1rem);
            max-width: calc(25% - 1rem);
          }
        }
        @media (min-width: 1200px) {
          .open-search-ecom-page .product-grid.with-filters .product-grid-item {
            flex: 1 1 calc(25% - 1rem);
            max-width: calc(25% - 1rem);
          }
          .open-search-ecom-page .product-grid.full-width .product-grid-item {
            flex: 1 1 calc(20% - 1rem);
            max-width: calc(20% - 1rem);
          }
        }
        @media (max-width: 991.98px) {
          .open-search-ecom-page .filter-panel-wrap {
            flex: 0 0 auto;
            max-width: 100%;
          }
          .open-search-settings-panel {
            width: 100% !important;
          }
          .open-search-ecom-page .smart-search-inline-wrap {
            flex-wrap: wrap;
          }
          .open-search-debug-panel {
            width: 100% !important;
          }
          .open-search-settings-resizer {
            display: none;
          }
          .open-search-ecom-page .search-filter-sticky {
            top: 0 !important;
          }
        }
      `}</style>
    </div>
  );
};

export default OpenSearchEcommerce;
